import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useContext, useLanguage } from '~/hooks'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { Button } from '~/components'
import heroUrl from '~/assets/img/Hero.webp'
import * as st from '~/assets/styl/Hero.module.styl'

function positionRatio(token: string | undefined, fallback: number) {
  if (!token) return fallback
  const value = token.trim().toLowerCase()
  if (value === 'left' || value === 'top') return 0
  if (value === 'center') return 0.5
  if (value === 'right' || value === 'bottom') return 1
  if (value.endsWith('%')) return parseFloat(value) / 100
  return fallback
}

export default function Hero() {
  const { openHamburger } = useContext()
  const headerRef = useRef<HTMLElement>(null)

  const mobile = Boolean(useBreakpoint().sm)
  const [height, setHeight] = useState(0)
  useEffect(() => {
    setHeight(mobile ? window.innerHeight : 0)
  }, [mobile])

  useLayoutEffect(() => {
    const header = headerRef.current
    const button = header?.querySelector<HTMLElement>(`.${st.btnLight}`)
    if (!header || !button) return

    const image = new Image()
    image.src = heroUrl

    const sync = () => {
      if (!image.naturalWidth) return
      const headerBox = header.getBoundingClientRect()
      const buttonBox = button.getBoundingClientRect()
      const boxW = headerBox.width
      const boxH = headerBox.height
      const position = getComputedStyle(header)
        .backgroundPosition.trim()
        .split(/\s+/)
      const posX = positionRatio(position[0], 0.5)
      const posY = positionRatio(position[1], 1)
      const scale = Math.max(
        boxW / image.naturalWidth,
        boxH / image.naturalHeight
      )
      const renderedW = image.naturalWidth * scale
      const renderedH = image.naturalHeight * scale
      const style = getComputedStyle(button)
      const originX =
        buttonBox.left - headerBox.left + parseFloat(style.borderLeftWidth)
      const originY =
        buttonBox.top - headerBox.top + parseFloat(style.borderTopWidth)
      button.style.setProperty('--hero-bg-w', `${renderedW}px`)
      button.style.setProperty('--hero-bg-h', `${renderedH}px`)
      button.style.setProperty(
        '--hero-bg-x',
        `${(boxW - renderedW) * posX - originX}px`
      )
      button.style.setProperty(
        '--hero-bg-y',
        `${(boxH - renderedH) * posY - originY}px`
      )
    }

    if (image.complete) sync()
    image.addEventListener('load', sync)
    const observer = new ResizeObserver(sync)
    observer.observe(header)
    observer.observe(button)
    window.addEventListener('resize', sync)
    return () => {
      image.removeEventListener('load', sync)
      observer.disconnect()
      window.removeEventListener('resize', sync)
    }
  }, [mobile, height])
  const { t } = useLanguage()
  const language = process.env.GATSBY_LANGUAGE || 'pt'

  return (
    <header
      id="Home"
      ref={headerRef}
      className={st.core}
      style={height ? { paddingTop: height } : null}
    >
      <h1 className={st.title}>{t('Hero.title')}</h1>
      <div className={st.overlay} />
      <div className={st.fixedWrap}>
        <Button href="/#FaleConosco" className={st.btnContact}>
          {t('Hero.buttonContact')}
        </Button>
        <div className={st.fixed}>
          <a
            href="/dvert/"
            className={language === 'pt' ? st.active : undefined}
          >
            PT
          </a>
          <a
            href="/en/dvert/"
            className={language === 'en' ? st.active : undefined}
          >
            EN
          </a>
          <a
            href="/es/dvert/"
            className={language === 'es' ? st.active : undefined}
          >
            ES
          </a>
          <button
            className={st.hamburger}
            onClick={() => openHamburger()}
          ></button>
        </div>
      </div>
      <div className={st.content}>
        <div className={st.textbox}>
          <h2>
            {t('Hero.heading')
              .split('\n')
              .map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
          </h2>
          <p>
            {t('Hero.subtitle')
              .split('\n')
              .map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
          </p>
          <Button href="/#FaleConosco" className={st.btnLight}>
            {t('Hero.buttonContact')}
          </Button>
        </div>
      </div>
    </header>
  )
}
